import { Hint } from "@common/components/shared/hint"
import { Button, type ButtonProps } from "@common/components/ui/button"
import { formatCount } from "@common/lib/number"
import { cn } from "@common/lib/utils"
import { forwardRef } from "react"
import ArchiveOutline from "~icons/material-symbols/archive-outline"
import Close from "~icons/material-symbols/close"
import UnarchiveOutline from "~icons/material-symbols/unarchive-outline"

export type BulkChange = {
  count: number
  pending: boolean
  onClick: () => void
}

const BAR = cn(
  "pointer-events-none absolute inset-x-0 bottom-0 z-10",
  "flex justify-center p-3",
)

const PANEL = cn(
  "pointer-events-auto flex h-10 items-center gap-1 rounded-lg border",
  "bg-background pr-1.5 pl-3 text-body-medium shadow-md",
)

export function FloatingActionBar({
  count,
  summary,
  archive,
  restore,
  onClear,
  children,
}: {
  count: number
  summary?: string
  archive?: BulkChange
  restore?: BulkChange
  onClear: () => void
  children?: React.ReactNode
}): React.ReactElement {
  return (
    <>
      <div className="h-16" />
      <div className={BAR}>
        <div className={PANEL}>
          <span className="mr-1.5 whitespace-nowrap">
            {formatCount(count)} selected
            {summary && (
              <span className="text-muted-foreground tabular-nums">
                {" "}
                · {summary}
              </span>
            )}
          </span>
          {children}
          {archive && archive.count > 0 && (
            <FloatingAction
              hint={`Archive ${archive.count}`}
              disabled={archive.pending}
              onClick={archive.onClick}
            >
              <ArchiveOutline />
              Archive
            </FloatingAction>
          )}
          {restore && restore.count > 0 && (
            <FloatingAction
              hint={`Restore ${restore.count}`}
              disabled={restore.pending}
              onClick={restore.onClick}
            >
              <UnarchiveOutline />
              Restore
            </FloatingAction>
          )}
          <Button
            variant="ghost"
            size="icon-sm"
            className="rounded-full text-muted-foreground"
            aria-label="Clear selection"
            onClick={onClear}
          >
            <Close />
          </Button>
        </div>
      </div>
    </>
  )
}

export const FloatingAction = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, "variant" | "size" | "className"> & {
    hint?: string | null
  }
>(function FloatingAction({ hint = null, ...props }, ref) {
  return (
    <Hint text={hint}>
      <Button
        ref={ref}
        variant="outline"
        size="sm"
        className="gap-2 rounded-full"
        {...props}
      />
    </Hint>
  )
})
